import {Controller} from "stimulus"

// Form-level controller for the unified contact relation form.
// Handles Enter key interception, contact typeahead selection,
// name change confirmation on submit, validation error checking,
// and contact reassignment when changing names on shared contacts.
export default class extends Controller {
    static targets = ["nameWarning", "selectDialog", "nameChangeDialog", "nameChangeMatches"]

    initialize() {
        this._nameChangeHandled = false
    }

    handleKeydown(e) {
        if (e.key !== "Enter") return
        if (e.target.tagName === "TEXTAREA") return
        if (e.target.tagName === "SELECT") return
        if (e.target.type === "submit") return

        e.preventDefault()
    }

    contactSelected(e) {
        e.preventDefault()
        e.stopPropagation()

        var resultEl = e.target.closest(".result-contact")
        if (!resultEl) return

        var contactId = resultEl.dataset.contactid
        var salutation = resultEl.dataset.salutation || ""
        var firstName = resultEl.dataset.firstName || ""
        var middleName = resultEl.dataset.middleName || ""
        var lastName = resultEl.dataset.lastName || ""
        var suffix = resultEl.dataset.suffix || ""

        var fieldset = this.element.querySelector(".contact-method-fieldset")
        if (!fieldset) return

        var idField = fieldset.querySelector(".contact-id-field")
        if (idField) idField.value = contactId

        this._setSelectValue(fieldset.querySelector("[id$='_salutation']"), salutation)
        this._setInputValue(fieldset.querySelector(".contact-first-name"), firstName)
        this._setInputValue(fieldset.querySelector("[id$='_middle_name']"), middleName)
        this._setInputValue(fieldset.querySelector(".contact-last-name"), lastName)
        this._setSelectValue(fieldset.querySelector("[id$='_suffix']"), suffix)

        // Update the original name data attributes so the name-change warning
        // reflects the newly-selected contact rather than the previous one.
        fieldset.dataset.originalSalutation = salutation
        fieldset.dataset.originalFirstName = firstName
        fieldset.dataset.originalMiddleName = middleName
        fieldset.dataset.originalLastName = lastName
        fieldset.dataset.originalSuffix = suffix

        fieldset.querySelectorAll(".contact-search-results").forEach(function (el) {
            el.classList.add("hidden")
        })

        this._loadContactMethods(contactId)
    }

    handleSubmit(e) {
        if (this._nameChangeHandled) return

        if (this._hasNameChangeWarning() && this._nameWasChanged()) {
            e.preventDefault()
            this._showNameChangeDialog()
            return
        }

        this._checkValidationErrors(e)
    }

    // Name change dialog: user chose to update name everywhere
    nameChangeUpdateAll() {
        this._hideNameChangeDialog()
        this._submitFormWithAction("update_all")
    }

    // Name change dialog: user chose to create a new contact
    nameChangeCreateNew() {
        this._hideNameChangeDialog()
        this._submitFormWithAction("create_new_contact")
    }

    // Name change dialog: user chose to link to an existing matching contact
    nameChangeLinkExisting(e) {
        var contactId = e.currentTarget.dataset.contactId
        this._hideNameChangeDialog()
        this._submitFormWithAction("link_existing", contactId)
    }

    // Name change dialog: cancel
    nameChangeCancel() {
        this._nameChangeHandled = false
        this._hideNameChangeDialog()
    }

    confirmMethodSelection() {
        if (!this._pendingMethods) return

        var selectedMethods = []
        var self = this
        var dialog = this.selectDialogTarget
        var checkboxes = dialog.querySelectorAll("input[type='checkbox']:checked")
        checkboxes.forEach(function (cb) {
            var index = parseInt(cb.dataset.methodIndex, 10)
            if (self._pendingMethods[index]) {
                selectedMethods.push(self._pendingMethods[index])
            }
        })

        this._hideSelectDialog()
        if (selectedMethods.length > 0) {
            this._populateContactMethods(selectedMethods)
        }
    }

    cancelMethodSelection() {
        this._hideSelectDialog()
    }

    _checkValidationErrors(e) {
        var validationErrors = this.element.querySelectorAll(".client-validation-error")
        var hasErrors = false
        validationErrors.forEach(function (el) {
            if (el.textContent.trim().length > 0) hasErrors = true
        })

        if (hasErrors) {
            if (confirm("Some contact methods have validation issues. OK to save and skip them, or Cancel to correct.")) {
                this._clearInvalidContactMethods()
            } else {
                e.preventDefault()
            }
        }
    }

    _submitFormWithAction(action, linkContactId) {
        var form = this.element.querySelector("form") || this.element.closest("form")
        if (!form) return

        this._nameChangeHandled = true

        // Add hidden field for the name change action
        var actionField = form.querySelector("input[name='name_change_action']")
        if (!actionField) {
            actionField = document.createElement("input")
            actionField.type = "hidden"
            actionField.name = "name_change_action"
            form.appendChild(actionField)
        }
        actionField.value = action

        if (linkContactId) {
            var linkField = form.querySelector("input[name='link_contact_id']")
            if (!linkField) {
                linkField = document.createElement("input")
                linkField.type = "hidden"
                linkField.name = "link_contact_id"
                form.appendChild(linkField)
            }
            linkField.value = linkContactId
        }

        form.requestSubmit()
    }

    _showNameChangeDialog() {
        if (!this.hasNameChangeDialogTarget) {
            // Fallback to simple confirm if dialog not present
            if (confirm("Changing this contact's name will update it for all linked records. Continue?")) {
                this._nameChangeHandled = true
                var form = this.element.querySelector("form") || this.element.closest("form")
                if (form) form.requestSubmit()
            }
            return
        }

        // Hide any open search result dropdowns so they don't bleed over the modal
        this.element.querySelectorAll(".contact-search-results").forEach(function (el) {
            el.classList.add("hidden")
        })

        var dialog = this.nameChangeDialogTarget
        dialog.classList.remove("hidden")

        // Search for matching contacts with the new name
        this._searchMatchingContacts()
    }

    _hideNameChangeDialog() {
        if (!this.hasNameChangeDialogTarget) return
        this.nameChangeDialogTarget.classList.add("hidden")
        if (this.hasNameChangeMatchesTarget) {
            this.nameChangeMatchesTarget.innerHTML = ""
        }
    }

    _searchMatchingContacts() {
        if (!this.hasNameChangeMatchesTarget) return

        var fieldset = this.element.querySelector(".contact-method-fieldset")
        if (!fieldset) return

        var firstName = (fieldset.querySelector(".contact-first-name") || {}).value || ""
        var lastName = (fieldset.querySelector(".contact-last-name") || {}).value || ""
        var searchName = (firstName + " " + lastName).trim()
        if (!searchName) return

        var self = this
        var matchesContainer = this.nameChangeMatchesTarget

        fetch("/contacts?name=" + encodeURIComponent(searchName))
            .then(function (response) { return response.text() })
            .then(function (html) {
                var parser = new DOMParser()
                var doc = parser.parseFromString(html, "text/html")
                var results = doc.querySelectorAll(".result-contact")

                // Filter out the current contact
                var currentContactId = ""
                var idField = fieldset.querySelector(".contact-id-field")
                if (idField) currentContactId = idField.value

                var matchCount = 0
                var fragment = document.createDocumentFragment()
                results.forEach(function (result) {
                    var cid = result.dataset.contactid
                    if (cid === currentContactId) return
                    matchCount++
                    var name = result.querySelector(".result-primary")
                    var nameText = name ? name.textContent.trim() : "Unknown"

                    var btn = document.createElement("button")
                    btn.type = "button"
                    btn.className = "w-full text-left px-3 py-2 text-sm border border-gray-200 rounded hover:bg-violet-50 flex items-center justify-between"
                    btn.dataset.action = "click->contact-relation-form-v3#nameChangeLinkExisting"
                    btn.dataset.contactId = cid

                    var nameSpan = document.createElement("span")
                    nameSpan.textContent = nameText
                    btn.appendChild(nameSpan)

                    var linkSpan = document.createElement("span")
                    linkSpan.className = "text-xs text-violet-600"
                    linkSpan.textContent = "Link to this contact"
                    btn.appendChild(linkSpan)

                    fragment.appendChild(btn)
                })

                matchesContainer.innerHTML = ""
                if (matchCount > 0) {
                    var heading = document.createElement("p")
                    heading.className = "text-xs text-gray-600 mb-2"
                    heading.textContent = "Or link to an existing contact instead:"
                    matchesContainer.appendChild(heading)
                    matchesContainer.appendChild(fragment)
                }
            })
            .catch(function () {
                matchesContainer.innerHTML = ""
            })
    }

    _nameWasChanged() {
        var fieldset = this.element.querySelector(".contact-method-fieldset")
        if (!fieldset) return false

        var fields = [
            { selector: ".contact-first-name", original: "originalFirstName" },
            { selector: ".contact-last-name", original: "originalLastName" },
            { selector: "[id$='_middle_name']", original: "originalMiddleName" },
            { selector: "[id$='_salutation']", original: "originalSalutation" },
            { selector: "[id$='_suffix']", original: "originalSuffix" }
        ]

        for (var i = 0; i < fields.length; i++) {
            var el = fieldset.querySelector(fields[i].selector)
            if (!el) continue
            var original = fieldset.dataset[fields[i].original]
            if (original === undefined) continue
            if (el.value !== original) return true
        }

        return false
    }

    _loadContactMethods(contactId) {
        var self = this
        fetch("/contacts/" + encodeURIComponent(contactId) + ".json")
            .then(function (response) {
                if (!response.ok) return null
                return response.json()
            })
            .then(function (data) {
                if (!data || !data.contact_methods || data.contact_methods.length === 0) return
                self._showSelectDialog(data, data.contact_methods)
            })
            .catch(function (error) {
                console.warn("Could not load contact methods:", error)
            })
    }

    _showSelectDialog(contact, methods) {
        this._pendingMethods = methods

        var dialog = this.selectDialogTarget
        var contactName = [contact.first_name, contact.last_name].filter(Boolean).join(" ").toUpperCase()
        dialog.querySelector(".contact-method-select-heading").textContent =
            contactName + ": choose any contact methods to copy for this record:"

        var optionsContainer = dialog.querySelector(".contact-method-select-options")
        optionsContainer.innerHTML = ""

        methods.forEach(function (cm, index) {
            var label = cm.display_string || (cm.medium + "(" + (cm.role || "") + "): " + (cm.connection_string || ""))
            var wrapper = document.createElement("div")
            wrapper.className = "flex items-center gap-2"

            var checkbox = document.createElement("input")
            checkbox.type = "checkbox"
            checkbox.id = "select-method-" + index
            checkbox.dataset.methodIndex = index
            checkbox.className = "rounded border-gray-300 text-violet-600 focus:ring-violet-500"

            var labelEl = document.createElement("label")
            labelEl.htmlFor = "select-method-" + index
            labelEl.className = "text-sm text-gray-700"
            labelEl.textContent = label

            wrapper.appendChild(checkbox)
            wrapper.appendChild(labelEl)
            optionsContainer.appendChild(wrapper)
        })

        dialog.classList.remove("hidden")
    }

    _hideSelectDialog() {
        this._pendingMethods = null
        var dialog = this.selectDialogTarget
        dialog.classList.add("hidden")
        dialog.querySelector(".contact-method-select-options").innerHTML = ""
    }

    _populateContactMethods(methods) {
        var self = this
        methods.forEach(function (cm) {
            var medium = cm.medium
            var sectionSelector = "[data-medium='" + medium + "']"
            var section = self.element.querySelector(sectionSelector)
            if (!section) return

            var emptyRow = self._findEmptyRow(section, medium)
            if (!emptyRow) {
                var addBtn = section.querySelector("[data-action*='contact-method-section#addRow']")
                if (addBtn) addBtn.click()
                emptyRow = self._findEmptyRow(section, medium)
            }
            if (!emptyRow) return

            self._populateRow(emptyRow, cm)
        })
    }

    _findEmptyRow(section, medium) {
        var rows = section.querySelectorAll("[data-target~='contact-method-section.row']")
        for (var i = 0; i < rows.length; i++) {
            var row = rows[i]
            if (row.style.display === "none") continue

            if (medium === "Mail") {
                var addressIdField = row.querySelector("input.address-id")
                var streetField = row.querySelector("[id*='street_one']")
                if ((!addressIdField || !addressIdField.value) && (!streetField || !streetField.value)) {
                    return row
                }
            } else {
                var connField = row.querySelector("[id*='connection_string']")
                if (connField && !connField.value) return row
            }
        }
        return null
    }

    _populateRow(row, cm) {
        var roleSelect = row.querySelector("[id*='_role']")
        if (roleSelect && cm.role) this._setSelectValue(roleSelect, cm.role)

        if (cm.medium === "Mail" && cm.address) {
            var addr = cm.address
            this._setInputValue(row.querySelector("[id*='street_one']"), addr.street_one || "")
            this._setInputValue(row.querySelector("[id*='street_two']"), addr.street_two || "")
            this._setInputValue(row.querySelector("[id*='street_three']"), addr.street_three || "")
            this._setInputValue(row.querySelector("[id*='city']"), addr.city || "")
            this._setInputValue(row.querySelector("[id*='state']"), addr.state || "")
            this._setInputValue(row.querySelector("[id*='zip']"), addr.zip || "")
        } else {
            var connField = row.querySelector("[id*='connection_string']")
            this._setInputValue(connField, cm.connection_string || "")
        }
    }

    _clearInvalidContactMethods() {
        var rows = this.element.querySelectorAll("[data-target~='contact-method-section.row']")
        rows.forEach(function (row) {
            var errorEl = row.querySelector(".client-validation-error")
            if (errorEl && errorEl.textContent.trim().length > 0) {
                var isPersisted = row.querySelector("input[type='hidden'][name$='[id]'][value]")
                if (isPersisted && isPersisted.value) {
                    // Persisted record: mark for destruction so Rails skips it
                    var destroyField = row.querySelector("input[name$='[_destroy]']")
                    if (destroyField) {
                        destroyField.value = "1"
                    }
                    row.style.display = "none"
                } else {
                    // New (unsaved) row: remove from DOM so it is not submitted
                    row.remove()
                }
                errorEl.textContent = ""
            }
        })
    }

    _setInputValue(input, value) {
        if (input) input.value = value
    }

    _setSelectValue(select, value) {
        if (!select) return
        var options = select.querySelectorAll("option")
        var matched = false
        for (var i = 0; i < options.length; i++) {
            if (options[i].value === value || options[i].text === value) {
                select.selectedIndex = i
                matched = true
                break
            }
        }
        if (!matched) select.selectedIndex = 0
    }

    _hasNameChangeWarning() {
        if (!this.hasNameWarningTarget) return false
        return !this.nameWarningTarget.classList.contains("hidden")
    }
}
